import React, { lazy } from 'react';
import IconException from '@/assets/images/menu/icon-exception.react.svg?react';
import { MultiApp } from '@/config/context';
import { isAndroid, isIOS, isWindows } from '@/infrastructure/product-info';
import { CapitalPlatform } from '@/infrastructure/utils/app';
import { isVersion } from '@/infrastructure/utils/version-strategy';
import { MenuIcon } from '@/presentation/components/menu-icon';
import { stores } from '@/presentation/store';
import { IRouteConfig } from '../types';
import { isGameType } from './game-performance';

/**
 * CPU 监控路由
 *
 * 平台：Android + Windows（对齐 OA master；此前专业版分支只留 Windows 且整组 hideInMenu，
 * 导致 Android 产品下 CPU 菜单完全不可见）。
 *
 * 版本可见性：
 * - OA / 子公司版（WOA / Internal）：按 master 行为在菜单中展示。
 * - 专业版（Pro / Dji / Sgp / Eu）：维持既有行为继续隐藏菜单，避免在后端能力
 *   与商业化策略未确认前对外暴露。路由本身仍注册，便于带链接直达与后续开放。
 *   TODO(merge-oa): 专业版是否开放 CPU 菜单待产品确认，确认后去掉这里的版本判断。
 */
const isCpuMenuVisibleVersion = (): boolean => isVersion(MultiApp.WOA, MultiApp.Internal);

/** 游戏性能已覆盖游戏类产品的 CPU 指标，避免菜单里出现两套口径 */
const isGamePerformanceCovered = (): boolean => {
  const gameSupportPlatform = isAndroid() || isIOS() || isWindows();
  return isGameType(stores.ProductStore.product?.type) && gameSupportPlatform;
};

export const cupRoutes = (cpuEnable: boolean): IRouteConfig[] => {
  // 仅 Android 和 Windows 平台开放 CPU 监控
  if (!isAndroid() && !isWindows()) {
    return [];
  }
  if (isGamePerformanceCovered()) {
    return [];
  }
  // Android 端直接强制启用，不受 cpuEnable（七彩石开关）控制
  const listEnabled = isAndroid() || cpuEnable;
  const menuVisible = isCpuMenuVisibleVersion();

  return [
    {
      path: '/cpu',
      name: 'CPU',
      icon: <MenuIcon icon={IconException} />,
      sort: 86,
      hideInMenu: !menuVisible,
      platforms: [
        CapitalPlatform.Android,
        CapitalPlatform.Windows,
      ],
      routes: [
        {
          path: '/cpu/analytics',
          name: '指标分析',
          component: lazy(() => import('@/presentation/pages/cpu/analytics')),
        },
        {
          path: '/cpu/list',
          name: '问题列表',
          hideInMenu: !listEnabled,
          disabled: !listEnabled,
          component: lazy(() => import('@/presentation/pages/cpu/list')),
        },
        {
          path: '/cpu/detail',
          name: '问题详情',
          component: lazy(() => import('@/presentation/pages/cpu/detail')),
          hideInMenu: true,
          whitelisted: true,
        },
        {
          path: '/cpu/drill-down',
          name: '个例分析',
          hideInMenu: !listEnabled,
          disabled: !listEnabled,
          component: lazy(() => import('@/presentation/pages/cpu/drill-down')),
        },
      ],
    },
  ];
};

export default cupRoutes;
